import React from "react";
import { useQuery } from "@apollo/client";
import { TextImageAlt as TextImageAltType } from "../../graphql/schema";
import {
  tokens,
  makeStyles,
  Text,
  mergeClasses,
} from "@fluentui/react-components";
import { GET_TEXTIMAGEALT } from "../../graphql/queries";
import { CtaButton } from "../common/input/CtaButton";
import Img from "../common/image/Img";
import { useNavigate } from "react-router-dom";
import { useCmsLocale } from "../../i18n/cmsLocale";

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    width: "95%",
    maxWidth: "1400px",
    marginLeft: "auto",
    marginRight: "auto",

    "@media (min-width: 1220px)": {
      flexDirection: "row",
      height: "min(80vh, 700px)",
    },
  },
  imageContainer: {
    width: "90%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingHorizontalM,
    paddingLeft: 0,
    paddingRight: 0,

    marginLeft: "auto",
    marginRight: "auto",
    "@media (min-width: 700px)": {
      width: "80%",
      paddingTop: tokens.spacingHorizontalXXL,
      paddingBottom: tokens.spacingHorizontalXXL,
    },
    "@media (min-width: 1220px)": {
      width: "100%",
      marginLeft: tokens.spacingHorizontalXL,
      paddingTop: tokens.spacingHorizontalXXXL,
      paddingBottom: tokens.spacingHorizontalXXXL,
    },
  },
  image: {
    width: "100%",
    objectFit: "contain",
    maxHeight: "100%",
    marginRight: tokens.spacingHorizontalXXXL,
    boxShadow: tokens.shadow64Brand,
  },
  textContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: tokens.colorBrandBackground,
    paddingTop: tokens.spacingHorizontalM,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,

    "@media (min-width: 700px)": {
      paddingTop: tokens.spacingHorizontalXXL,
      paddingBottom: tokens.spacingHorizontalXXL,
    },
    "@media (min-width: 1220px)": {
      width: "70%",
      marginLeft: tokens.spacingHorizontalXXXL,
      paddingTop: tokens.spacingHorizontalXXXL,
      paddingBottom: tokens.spacingHorizontalXXXL,
    },
  },
  descriptionWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  titleWrapper: {
    display: "flex",
    flexDirection: "column",
    marginBottom: tokens.spacingVerticalL,
    "@media (min-width: 700px)": {
      marginBottom: tokens.spacingVerticalXL,
    },
  },
  marginBottom: {
    marginBottom: tokens.spacingVerticalL,
    "@media (min-width: 700px)": {
      marginBottom: tokens.spacingVerticalXXXL,
    },
  },
  descriptionText: {
    fontSize: tokens.fontSizeBase300,
    "@media (min-width: 700px)": {
      fontSize: tokens.fontSizeBase500,
    },
  },
});
export const TextImageAlt = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const locale = useCmsLocale();
  const { data, loading, error } = useQuery(GET_TEXTIMAGEALT, { variables: { locale } });

  if (loading || error || !data.textImageAlt) return <></>;
  const textImageData: TextImageAltType = data.textImageAlt;

  return (
    <article className={classes.wrapper}>
      <div className={classes.imageContainer}>
        <Img
          className={classes.image}
          src={textImageData.image?.url ?? ""}
          alt={textImageData.image?.alternativeText ?? ""}
        ></Img>
      </div>
      <div className={classes.textContainer}>
        <div className={classes.titleWrapper}>
          <Text size={800} weight="bold" align="center">
            {textImageData.title ?? ""}
          </Text>
          <Text size={600} weight="medium" align="center">
            {textImageData.subTitle ?? ""}
          </Text>
        </div>
        {textImageData.description && (
          <div
            className={mergeClasses(
              classes.descriptionWrapper,
              classes.marginBottom
            )}
          >
            <Text align="center" size={500} className={mergeClasses(classes.marginBottom, classes.descriptionText)}>
              {textImageData.description}
            </Text>
            <Text align="center" size={500} className={mergeClasses(classes.marginBottom, classes.descriptionText)}>
              {textImageData.descriptionPt2 ?? ""}
            </Text>
            <Text align="center" size={500} className={classes.descriptionText}>
              {textImageData.descriptionPt3 ?? ""}
            </Text>
          </div>
        )}
        {textImageData.buttonText && (
          <CtaButton
            buttonText={textImageData.buttonText}
            onClick={() => navigate("/faq")}
          />
        )}
      </div>
    </article>
  );
};
